import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from "@mui/material";
import { api } from "common";
import MaterialTable from "material-table";
import moment from "moment/min/moment-with-locales";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { downloadCsv } from "../common/sharedFunctions";
import AlertDialog from "../components/AlertDialog";
import { colors } from "../components/Theme/WebTheme";
import WalletHistoryModal from "../components/WalletHistoryModal";
import { FirebaseConfig } from "../config/FirebaseConfig";
import { IconButton } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";

const Withdraws = () => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir();
    const settings = useSelector((state) => state.settingsdata.settings);
    const providers = useSelector((state) => state.paymentmethods.providers);
    const { completeWithdraw, processWithdraw, fetchWithdraws } = api;
    const dispatch = useDispatch();
    const addons = useSelector((state) => state.addonsdata.addons);

    const [payFound, setPayFound] = useState([]);
    const [walletHistoryModalOpen, setWalletHistoryModalOpen] = useState(false);
    const [walletHistoryModalProps, setWalletHistoryModalProps] = useState({
        userId: null,
        withdrawAmount: 0,
        userName: "",
        withdrawDate: null,
    });

    const handleSeeHistory = (rowData) => {
        // Determine userId from row
        const userId =
            rowData.uid || rowData.requestedBy || rowData.userId || null;
        // Determine withdraw date with precise time (prefer approvedAt/approvedDate/requestTimestamp before others)
        const withdrawDate = rowData.requestTimestamp;
        const userName = rowData.name || rowData.driver_name || "";
        const withdrawAmount = parseFloat(rowData.amount || 0);

        setWalletHistoryModalProps({
            userId,
            withdrawAmount,
            userName,
            withdrawDate,
        });
        setWalletHistoryModalOpen(true);
    };
    useEffect(() => {
        if (providers && providers.length > 0) {
            for (let i = 0; i < providers.length; i++) {
                if (providers[i].withdraw_enable) {
                    setPayFound(providers[i]);
                }
            }
        }
    }, [providers]);

    const columns = [
        {
            title: t("transaction_id") || "Transaction ID",
            field: "transactionId",
            editable: "never",
            filterable: true,
            filterPlaceholder:
                t("filter_transaction_id") || "Filter Transaction ID",
            render: (rowData) => (
                <div style={{ fontFamily: "monospace", fontSize: "12px" }}>
                    {rowData.transactionId ||
                        rowData.transaction_id ||
                        rowData.id ||
                        "N/A"}
                </div>
            ),
        },
        {
            title: t("driver") || "Conductor",
            field: "name",
            editable: "never",
            render: (rowData) => (
                <div>
                    <div style={{ fontWeight: "bold", fontSize: "16px" }}>
                        {rowData.name || rowData.driver_name || "N/A"}
                    </div>
                    <div style={{ fontSize: "12px", color: "#666" }}>
                        {rowData.requestedByEmail || rowData.email || "N/A"}
                    </div>
                    <div
                        style={{
                            fontSize: "11px",
                            color: "#888",
                            fontStyle: "italic",
                        }}
                    >
                        {rowData.userType || "N/A"} •{" "}
                        {rowData.requestedByMobile || "N/A"}
                    </div>
                </div>
            ),
        },
        {
            title: t("amount") || "Monto",
            field: "amount",
            editable: "never",
            cellStyle: { textAlign: "center" },
            headerStyle: { textAlign: "center" },
            render: (rowData) => (
                <span style={{ fontWeight: "bold" }}>
                    ${parseFloat(rowData.amount || 0).toFixed(2)}
                </span>
            ),
        },
        {
            title: t("request_date") || "Request Date",
            field: "date",
            editable: "never",
            cellStyle: { textAlign: "center" },
            headerStyle: { textAlign: "center" },
            render: (rowData) => (
                <div style={{ textAlign: "center" }}>
                    <div style={{ fontWeight: "bold", fontSize: "12px" }}>
                        {rowData.requestDate ||
                            (rowData.date
                                ? moment(rowData.date).format("YYYY-MM-DD")
                                : "N/A")}
                    </div>
                </div>
            ),
        },
        {
            title: t("status") || "Status",
            field: "processed",
            editable: "never",
            cellStyle: { textAlign: "center" },
            headerStyle: { textAlign: "center" },
            render: (rowData) => {
                // Handle different status values from new implementation
                const status = rowData.processed;
                const isPending =
                    status === "pending" || status === false || status === null;
                const isApproved =
                    status === "approved" ||
                    status === true ||
                    status === "completed";
                const isRejected = status === "rejected";

                if (isPending) {
                    return (
                        <span
                            style={{
                                backgroundColor: "#fff3cd",
                                color: "#856404",
                                padding: "4px 12px",
                                borderRadius: "20px",
                                fontSize: "12px",
                                display: "inline-flex",
                                alignItems: "center",
                            }}
                        >
                            <span style={{ marginRight: "4px" }}>⏰</span>
                            {t("pending") || "Pending"}
                        </span>
                    );
                } else if (isApproved) {
                    return (
                        <div style={{ textAlign: "center" }}>
                            <span
                                style={{
                                    backgroundColor: "#d4edda",
                                    color: "#155724",
                                    padding: "4px 12px",
                                    borderRadius: "20px",
                                    fontSize: "12px",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    marginBottom: "4px",
                                }}
                            >
                                <span style={{ marginRight: "4px" }}>✅</span>
                                {t("approved") || "Approved"}
                            </span>

                            {/* Approval Date */}
                            {rowData.approvedAt && (
                                <div
                                    style={{
                                        fontSize: "10px",
                                        color: "#28a745",
                                        padding: "2px 6px",
                                        borderRadius: "4px",
                                        marginTop: "2px",
                                    }}
                                >
                                    📅{" "}
                                    {moment(rowData.approvedAt).format(
                                        "YYYY-MM-DD HH:mm"
                                    )}
                                </div>
                            )}
                        </div>
                    );
                } else if (isRejected) {
                    return (
                        <div style={{ textAlign: "center" }}>
                            <span
                                style={{
                                    backgroundColor: "#f8d7da",
                                    color: "#721c24",
                                    padding: "4px 12px",
                                    borderRadius: "20px",
                                    fontSize: "12px",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    marginBottom: "4px",
                                }}
                            >
                                <span style={{ marginRight: "4px" }}>❌</span>
                                {t("rejected") || "Rejected"}
                            </span>

                            {/* Rejection Date */}
                            {rowData.rejectedAt && (
                                <div
                                    style={{
                                        fontSize: "10px",
                                        color: "#dc3545",
                                        padding: "2px 6px",
                                        borderRadius: "4px",
                                        marginTop: "2px",
                                    }}
                                >
                                    📅{" "}
                                    {moment(rowData.rejectedAt).format(
                                        "YYYY-MM-DD HH:mm"
                                    )}
                                </div>
                            )}
                        </div>
                    );
                } else {
                    return (
                        <span
                            style={{
                                backgroundColor: "#e2e3e5",
                                color: "#383d41",
                                padding: "4px 12px",
                                borderRadius: "20px",
                                fontSize: "12px",
                            }}
                        >
                            {t("unknown") || "Unknown"}
                        </span>
                    );
                }
            },
        },
        ...(addons?.showWalletHistory
            ? [
                  {
                      title: t("history") || "Historial",
                      field: "history",
                      editable: "never",
                      cellStyle: { textAlign: "center" },
                      headerStyle: { textAlign: "center" },
                      render: (rowData) => (
                          <>
                              {rowData.processed !== "approved" ? (
                                  <IconButton
                                      onClick={() => handleSeeHistory(rowData)}
                                      size="small"
                                      color="primary"
                                      title={t("see") || "See"}
                                      sx={{
                                          backgroundColor: "#1976d2",
                                          color: "white",
                                          "&:hover": {
                                              backgroundColor: "#115293",
                                          },
                                      }}
                                  >
                                      <VisibilityIcon fontSize="small" />
                                  </IconButton>
                              ) : (
                                  "-"
                              )}
                          </>
                      ),
                  },
              ]
            : []),
        {
            title: t("account") || "Account",
            field: "uid",
            editable: "never",
            cellStyle: { textAlign: "center" },
            headerStyle: { textAlign: "center" },
            render: (rowData) => {
                // Check if bank data is available
                const hasBankData =
                    rowData.bankData && rowData.bankData.accountNumber;

                return (
                    <div style={{ textAlign: "center" }}>
                        {/* User ID (clickable untuk details) */}
                        <div
                            style={{
                                fontFamily: "monospace",
                                fontSize: "11px",
                                color: "#1976d2",
                                fontWeight: "bold",
                                cursor: "pointer",
                                textDecoration: "underline",
                                padding: "4px 8px",
                                borderRadius: "4px",
                                transition: "background-color 0.2s",
                                marginBottom: "4px",
                            }}
                            onClick={() =>
                                handleUserDetailsClick(
                                    rowData.uid || rowData.requestedBy
                                )
                            }
                            title="Click to view user details"
                        >
                            {hasBankData && (
                                <div
                                    style={{
                                        fontSize: "10px",
                                        color: "#28a745",
                                        padding: "2px 6px",
                                        borderRadius: "4px",
                                        fontFamily: "monospace",
                                    }}
                                >
                                    {rowData.bankData.accountNumber || "N/A"}
                                </div>
                            )}
                        </div>
                    </div>
                );
            },
        },
        {
            title: t("actions") || "Acciones",
            field: "actions",
            editable: "never",
            cellStyle: { textAlign: "center" },
            headerStyle: { textAlign: "center" },
            render: (rowData) => {
                // Handle different status values from new implementation
                const status = rowData.processed;
                const isPending =
                    status === "pending" || status === false || status === null;
                const isApproved =
                    status === "approved" ||
                    status === true ||
                    status === "completed";
                const isRejected = status === "rejected";

                if (isPending) {
                    return (
                        <div
                            style={{
                                display: "flex",
                                gap: "8px",
                                justifyContent: "center",
                            }}
                        >
                            <button
                                onClick={() => handleApprove(rowData)}
                                style={{
                                    backgroundColor: "#4caf50",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "20px",
                                    padding: "8px 16px",
                                    cursor: "pointer",
                                    fontSize: "12px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                }}
                            >
                                <span>✓</span>
                                {t("approve") || "Approve"}
                            </button>
                            <button
                                onClick={() => handleReject(rowData)}
                                style={{
                                    backgroundColor: "#f44336",
                                    color: "white",
                                    border: "none",
                                    padding: "8px 16px",
                                    cursor: "pointer",
                                    fontSize: "12px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                }}
                            >
                                <span>✗</span>
                                {t("reject") || "Reject"}
                            </button>
                        </div>
                    );
                } else if (isApproved) {
                    return (
                        <div style={{ textAlign: "center" }}>
                            <span
                                style={{
                                    color: "#28a745",
                                    fontSize: "12px",
                                    fontWeight: "bold",
                                }}
                            >
                                ✅ {t("approved") || "Approved"}
                            </span>
                            {/* Approved Date */}
                            {rowData.approvedAt && (
                                <div
                                    style={{
                                        fontSize: "10px",
                                        color: "#28a745",
                                        padding: "2px 6px",
                                        marginTop: "2px",
                                    }}
                                >
                                    📅{" "}
                                    {moment(rowData.approvedAt).format(
                                        "YYYY-MM-DD HH:mm"
                                    )}
                                </div>
                            )}
                        </div>
                    );
                } else if (isRejected) {
                    return (
                        <div style={{ textAlign: "center" }}>
                            <span
                                style={{
                                    color: "#dc3545",
                                    fontSize: "12px",
                                    fontWeight: "bold",
                                }}
                            >
                                ❌ {t("rejected") || "Rejected"}
                            </span>
                            {/* Rejection Date */}
                            {rowData.rejectedAt && (
                                <div
                                    style={{
                                        fontSize: "10px",
                                        color: "#dc3545",
                                        padding: "2px 6px",
                                        marginTop: "2px",
                                    }}
                                >
                                    📅{" "}
                                    {moment(rowData.rejectedAt).format(
                                        "YYYY-MM-DD HH:mm"
                                    )}
                                </div>
                            )}
                        </div>
                    );
                } else {
                    return (
                        <span style={{ color: "#666", fontSize: "12px" }}>
                            {t("unknown_status") || "Unknown Status"}
                        </span>
                    );
                }
            },
        },
    ];
    const [data, setData] = useState([]);
    const withdrawdata = useSelector((state) => state.withdrawdata);

    // Refresh states
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [lastRefresh, setLastRefresh] = useState(Date.now());
    const [lastRefreshTime, setLastRefreshTime] = useState(
        new Date().toLocaleTimeString()
    );
    const [newDataNotification, setNewDataNotification] = useState({
        show: false,
        count: 0,
    });

    // Filter states
    const [statusFilter, setStatusFilter] = useState("");
    const [dateFilter, setDateFilter] = useState("");
    const [amountFilter, setAmountFilter] = useState("");
    const [searchText, setSearchText] = useState("");
    const [filteredData, setFilteredData] = useState([]);

    // Refresh function with useCallback for stability
    const refreshData = useCallback(
        async (showLoading = false) => {
            if (showLoading) {
                setIsRefreshing(true);
            }

            try {
                console.log("🔄 Refreshing withdraws data...");
                await dispatch(fetchWithdraws());

                // Update refresh time
                const now = new Date();
                setLastRefresh(now.getTime());
                setLastRefreshTime(now.toLocaleTimeString());

                console.log(
                    "✅ Data refreshed successfully at:",
                    now.toLocaleTimeString()
                );

                // Check if new data was added
                if (
                    withdrawdata.withdraws &&
                    withdrawdata.withdraws.length > data.length
                ) {
                    const newCount =
                        withdrawdata.withdraws.length - data.length;
                    // setNewDataNotification({ show: true, count: newCount });

                    setTimeout(() => {
                        // setNewDataNotification({ show: false, count: 0 });
                    }, 10);
                }
            } catch (error) {
                console.error("❌ Error refreshing data:", error);
            } finally {
                if (showLoading) {
                    setIsRefreshing(false);
                }
            }
        },
        [dispatch, withdrawdata.withdraws, data.length]
    );

    // Initialize data when component mounts and monitor for changes
    useEffect(() => {
        console.log(
            "📊 Data changed:",
            withdrawdata.withdraws?.length || 0,
            "items"
        );

        if (withdrawdata.withdraws) {
            // Check if new data was added
            if (
                withdrawdata.withdraws.length > data.length &&
                data.length > 0
            ) {
                const newCount = withdrawdata.withdraws.length - data.length;
                console.log("🆕 New data detected!", newCount, "new items");

                // Show notification
                // setNewDataNotification({ show: true, count: newCount });

                // Hide notification after 5 seconds
                setTimeout(() => {
                    // setNewDataNotification({ show: false, count: 0 });
                }, 10);
            }

            setData(withdrawdata.withdraws);
        } else {
            setData([]);
        }
    }, [withdrawdata.withdraws, data.length]);

    // Load initial data and setup real-time monitoring
    useEffect(() => {
        console.log("🔌 Setting up real-time monitoring...");

        // Load initial data
        dispatch(fetchWithdraws());

        // Set up interval to check for new data every 30 seconds
        const dataCheckInterval = setInterval(() => {
            console.log("🔄 Checking for new data...");
            dispatch(fetchWithdraws());
        }, 30000); // Check every 30 seconds

        // Cleanup interval on unmount
        return () => {
            console.log("🔌 Cleaning up real-time monitoring...");
            clearInterval(dataCheckInterval);
        };
    }, [dispatch]); // Only dispatch dependency

    // Filter data when filters change
    useEffect(() => {
        let filtered = [...data];

        // Search filter
        if (searchText && searchText.trim() !== "") {
            const searchLower = searchText.toLowerCase();
            filtered = filtered.filter((item) => {
                // Search in multiple fields
                return (
                    // User info
                    (item.name &&
                        item.name.toLowerCase().includes(searchLower)) ||
                    (item.driver_name &&
                        item.driver_name.toLowerCase().includes(searchLower)) ||
                    (item.requestedByEmail &&
                        item.requestedByEmail
                            .toLowerCase()
                            .includes(searchLower)) ||
                    (item.email &&
                        item.email.toLowerCase().includes(searchLower)) ||
                    (item.requestedByMobile &&
                        item.requestedByMobile
                            .toLowerCase()
                            .includes(searchLower)) ||
                    (item.userType &&
                        item.userType.toLowerCase().includes(searchLower)) ||
                    // Transaction info
                    (item.transactionId &&
                        item.transactionId
                            .toLowerCase()
                            .includes(searchLower)) ||
                    (item.transaction_id &&
                        item.transaction_id
                            .toLowerCase()
                            .includes(searchLower)) ||
                    (item.id &&
                        item.id
                            .toString()
                            .toLowerCase()
                            .includes(searchLower)) ||
                    // Amount
                    (item.amount &&
                        item.amount.toString().includes(searchText)) ||
                    // Bank info
                    (item.bankData &&
                        item.bankData.accountNumber &&
                        item.bankData.accountNumber
                            .toLowerCase()
                            .includes(searchLower)) ||
                    (item.bankData &&
                        item.bankData.bankName &&
                        item.bankData.bankName
                            .toLowerCase()
                            .includes(searchLower))
                );
            });
        }

        // Status filter
        if (statusFilter) {
            filtered = filtered.filter((item) => {
                const status = item.processed;
                if (statusFilter === "pending") {
                    return (
                        status === "pending" ||
                        status === false ||
                        status === null
                    );
                } else if (statusFilter === "approved") {
                    return (
                        status === "approved" ||
                        status === true ||
                        status === "completed"
                    );
                } else if (statusFilter === "rejected") {
                    return status === "rejected";
                }
                return true;
            });
        }

        // Date filter
        if (dateFilter) {
            const now = new Date();
            const today = new Date(
                now.getFullYear(),
                now.getMonth(),
                now.getDate()
            );
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);
            const weekStart = new Date(today);
            weekStart.setDate(weekStart.getDate() - weekStart.getDay());
            const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
            const lastMonthStart = new Date(
                now.getFullYear(),
                now.getMonth() - 1,
                1
            );

            filtered = filtered.filter((item) => {
                const itemDate = new Date(
                    item.date ||
                        item.requestDate ||
                        item.approvedAt ||
                        item.rejectedAt
                );

                switch (dateFilter) {
                    case "today":
                        return itemDate >= today;
                    case "yesterday":
                        return itemDate >= yesterday && itemDate < today;
                    case "week":
                        return itemDate >= weekStart;
                    case "month":
                        return itemDate >= monthStart;
                    case "last_month":
                        return (
                            itemDate >= lastMonthStart && itemDate < monthStart
                        );
                    default:
                        return true;
                }
            });
        }

        // Amount filter
        if (amountFilter) {
            filtered = filtered.filter((item) => {
                const amount = parseFloat(item.amount || 0);

                switch (amountFilter) {
                    case "0-100":
                        return amount >= 0 && amount <= 100;
                    case "100-500":
                        return amount > 100 && amount <= 500;
                    case "500-1000":
                        return amount > 500 && amount <= 1000;
                    case "1000+":
                        return amount > 1000;
                    default:
                        return true;
                }
            });
        }

        setFilteredData(filtered);
    }, [data, statusFilter, dateFilter, amountFilter, searchText]);

    // Clear all filters
    const clearFilters = () => {
        setStatusFilter("");
        setDateFilter("");
        setAmountFilter("");
        setSearchText("");
    };

    const [selectedRow, setSelectedRow] = useState(null);
    const [commonAlert, setCommonAlert] = useState({ open: false, msg: "" });
    const [userDetailsModal, setUserDetailsModal] = useState({
        open: false,
        userData: null,
        bankData: null,
    });

    // State untuk modal confirm dan alerts
    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        action: null, // 'approve' atau 'reject'
        withdraw: null,
        reason: "",
    });

    const [alertDialog, setAlertDialog] = useState({
        open: false,
        title: "",
        message: "",
        type: "success",
    });

    const cancelOperation = () => {
        setCommonAlert({ open: false, msg: "" });
    };

    // Functions untuk handle modal confirm dan alerts
    const showAlert = (title, message, type = "success") => {
        setAlertDialog({
            open: true,
            title,
            message,
            type,
        });
    };

    const closeAlert = () => {
        setAlertDialog({
            ...alertDialog,
            open: false,
        });
    };

    const handleConfirmClick = (withdraw, action) => {
        setConfirmDialog({
            open: true,
            action,
            withdraw,
            reason: "",
        });
    };

    const handleConfirmClose = () => {
        setConfirmDialog({
            open: false,
            action: null,
            withdraw: null,
            reason: "",
        });
    };

    const handleConfirmAction = async () => {
        const { action, withdraw, reason } = confirmDialog;

        if (action === "reject" && !reason.trim()) {
            showAlert(
                t("error") || "Error",
                t("rejection_reason_required") ||
                    "Rejection reason is required",
                "error"
            );
            return;
        }

        try {
            // Close dialog first
            handleConfirmClose();

            // Get admin data from auth context or props
            const adminData = {
                id: "admin_user_id", // Replace with actual admin ID
                name: "Admin User", // Replace with actual admin name
            };

            // Process the action
            let result;
            if (action === "approve") {
                result = await dispatch(
                    processWithdraw(withdraw.id, "approve", adminData)
                );
            } else {
                result = await dispatch(
                    processWithdraw(
                        withdraw.id,
                        "reject",
                        adminData,
                        reason.trim()
                    )
                );
            }

            if (result.success) {
                showAlert(
                    t("success") || "Success",
                    action === "approve"
                        ? t("withdrawal_approved") ||
                              "Withdrawal approved successfully"
                        : t("withdrawal_rejected") ||
                              "Withdrawal rejected successfully",
                    "success"
                );

                // Refresh data
                // Note: You might need to implement a refresh function or dispatch fetchWithdraws
            } else {
                showAlert(
                    t("error") || "Error",
                    result.error || t("action_failed") || "Action failed",
                    "error"
                );
            }
        } catch (error) {
            console.error("Error processing withdrawal:", error);
            showAlert(
                t("error") || "Error",
                t("action_failed") || "Action failed",
                "error"
            );
        }
    };

    // Function untuk fetch user dan bank details menggunakan HTTP Firebase function
    const handleUserDetailsClick = async (userId) => {
        try {
            // Find user data dari existing data (tidak perlu fetch lagi)
            const userWithdraw = data.find(
                (item) => (item.uid || item.requestedBy) === userId
            );

            if (userWithdraw && userWithdraw.bankData) {
                // Data sudah tersedia, langsung tampilkan modal
                console.log("✅ Using existing data for user:", userId);
                setUserDetailsModal({
                    open: true,
                    userData: {
                        uid: userWithdraw.uid || userWithdraw.requestedBy,
                        firstName: userWithdraw.name?.split(" ")[0] || "N/A",
                        lastName:
                            userWithdraw.name?.split(" ").slice(1).join(" ") ||
                            "N/A",
                        email: userWithdraw.requestedByEmail || "N/A",
                        mobile: userWithdraw.requestedByMobile || "N/A",
                        usertype: userWithdraw.userType || "N/A",
                        walletBalance: userWithdraw.currentBalance || 0,
                        currency: userWithdraw.currency || "USD",
                    },
                    bankData: userWithdraw.bankData,
                });
            } else {
                // Fallback: fetch data jika tidak tersedia
                console.log("🔍 Fetching details for user:", userId);

                // Get Firebase project ID from config
                const projectId = FirebaseConfig.projectId;
                const region = "us-central1"; // Default region

                // Call the getUserDetails HTTP function
                console.log("🔍 Calling getUserDetails HTTP function...");
                const response = await fetch(
                    `https://${region}-${projectId}.cloudfunctions.net/getUserDetailsHttp`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({ userId }),
                    }
                );

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(
                        `HTTP ${response.status}: ${
                            errorData.error || "Unknown error"
                        }`
                    );
                }

                const result = await response.json();
                console.log("📥 Function response:", result);

                if (result.success) {
                    console.log("✅ User details fetched successfully");
                    setUserDetailsModal({
                        open: true,
                        userData: result.userData,
                        bankData: result.bankData,
                    });
                } else {
                    console.error("❌ Function returned error:", result.error);
                    alert("Error: " + result.error);
                }
            }
        } catch (error) {
            console.error("❌ Error handling user details:", error);
            console.error("❌ Error details:", {
                message: error.message,
                code: error.code,
                name: error.name,
                stack: error.stack,
            });

            // More specific error messages
            if (error.message.includes("Failed to fetch")) {
                alert("Network error. Please check your internet connection.");
            } else if (error.message.includes("HTTP 404")) {
                alert("User not found. Please check the User ID.");
            } else if (error.message.includes("HTTP 500")) {
                alert("Server error. Please try again later.");
            } else {
                alert("Error fetching user details: " + error.message);
            }
        }
    };

    const closeUserDetailsModal = () => {
        setUserDetailsModal({ open: false, userData: null, bankData: null });
    };

    // Manual refresh function with useCallback
    const handleManualRefresh = useCallback(async () => {
        setIsRefreshing(true);
        try {
            await refreshData(true);

            // Show success feedback
            // setNewDataNotification({
            //   show: true,
            //   count: 0,
            //   message: 'Data refreshed manually'
            // });

            setTimeout(() => {
                // setNewDataNotification({ show: false, count: 0 });
            }, 10);
        } catch (error) {
            console.error("❌ Manual refresh failed:", error);
        } finally {
            setIsRefreshing(false);
        }
    }, [refreshData]);

    const handleApprove = async (rowData) => {
        handleConfirmClick(rowData, "approve");
    };

    const handleReject = async (rowData) => {
        handleConfirmClick(rowData, "reject");
    };

    const doWithdraw = async () => {
        if (payFound && payFound.public_key.length > 0) {
            const batchArr = [];
            for (let i = 0; i < data.length; i++) {
                if (payFound && payFound.id === "stripe") {
                    if (
                        data[i].hasOwnProperty("stripe_account_id") &&
                        !data.processed
                    ) {
                        const body = {
                            amount: data[i].amount,
                            currency: data[i].currency,
                            stripe_account_id: data[i].stripe_account_id,
                        };
                        try {
                            const response = await fetch(
                                `https://${FirebaseConfig.projectId}.web.app/stripe-transfer`,
                                {
                                    method: "POST",
                                    headers: {
                                        "Content-Type": "application/json",
                                    },
                                    body: JSON.stringify(body),
                                }
                            );
                            const json = await response.json();
                            if (json.error) {
                                dispatch(
                                    completeWithdraw(
                                        {
                                            id: data[i].id,
                                            error: json.error,
                                        },
                                        false
                                    )
                                );
                            } else {
                                dispatch(
                                    completeWithdraw(
                                        {
                                            ...data[i],
                                            transfer_id: json.transfer.id,
                                            error: "",
                                        },
                                        true
                                    )
                                );
                            }
                        } catch (err) {
                            dispatch(
                                completeWithdraw(
                                    {
                                        id: data[i].id,
                                        error: err.toString(),
                                    },
                                    false
                                )
                            );
                        }
                    }
                } else {
                    if (
                        data[i].hasOwnProperty("paypal_email") &&
                        !data.processed
                    ) {
                        batchArr.push({
                            amount: {
                                value: data[i].amount,
                                currency: data[i].currency,
                            },
                            sender_item_id: data[i].id,
                            recipient_wallet: "PAYPAL",
                            receiver: data[i].paypal_email,
                        });
                    }
                }
            }
            if (batchArr.length > 0) {
                const payoutData = {
                    data: {
                        sender_batch_header: {
                            sender_batch_id: "Payouts_" + new Date().getTime(),
                            email_subject: "You have a payout!",
                            email_message:
                                "You have received a payout! Thanks for using our service!",
                            recipient_type: "EMAIL",
                        },
                        items: batchArr,
                    },
                };
                const response = await fetch(
                    `https://${FirebaseConfig.projectId}.web.app/paypal-transfer`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify(payoutData),
                    }
                );
                const json = await response.json();
                if (json.success) {
                    for (let i = 0; i < batchArr.length; i++) {
                        let item = batchArr[i];
                        dispatch(
                            completeWithdraw(
                                {
                                    id: item.sender_item_id,
                                    payout_batch_id:
                                        json.payout_details.batch_header
                                            .payout_batch_id,
                                    error: "",
                                },
                                true
                            )
                        );
                    }
                } else {
                    for (let i = 0; i < batchArr.length; i++) {
                        let item = batchArr[i];
                        dispatch(
                            completeWithdraw(
                                {
                                    id: item.sender_item_id,
                                    error: json.error,
                                },
                                false
                            )
                        );
                    }
                }
            }
            alert(t("success"));
        } else {
            setCommonAlert({ open: true, msg: t("provider_not_found") });
        }
    };

    return (
        <div>
            <div style={{ borderRadius: "19px", padding: 10 }}>
                {/* New Data Notification */}
                {newDataNotification.show && (
                    <div
                        style={{
                            position: "fixed",
                            top: "20px",
                            right: "20px",
                            backgroundColor: "#28a745",
                            color: "white",
                            padding: "12px 20px",
                            borderRadius: "8px",
                            boxShadow: "0 4px 12px rgba(40, 167, 69, 0.3)",
                            zIndex: 1000,
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            transform: "translateX(0)",
                            transition: "all 0.3s ease-out",
                            opacity: 1,
                        }}
                    >
                        <span style={{ fontSize: "18px" }}>🆕</span>
                        <div>
                            <div style={{ fontWeight: "600" }}>
                                New Data Available!
                            </div>
                            <div style={{ fontSize: "12px", opacity: 0.9 }}>
                                {newDataNotification.count} new withdraw
                                {newDataNotification.count > 1 ? "s" : ""} added
                            </div>
                        </div>
                    </div>
                )}

                {/* Header with Title and Refresh Button */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        margin: "20px 20px 20px 15px",
                    }}
                >
                    <Typography
                        variant="h4"
                        style={{
                            textAlign: isRTL === "rtl" ? "right" : "left",
                        }}
                    >
                        {t("withdraw")}
                    </Typography>
                </div>

                {/* Statistics Dashboard */}
                <div
                    style={{
                        margin: "20px 15px",
                        display: "grid",
                        gridTemplateColumns:
                            "repeat(auto-fit, minmax(250px, 1fr))",
                        gap: "16px",
                        maxWidth: "100%",
                    }}
                >
                    {/* Pending Card */}
                    <div
                        style={{
                            backgroundColor: "white",
                            borderRadius: "12px",
                            padding: "16px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                            border: "1px solid #e9ecef",
                            minHeight: "100px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                                flexWrap: "wrap",
                            }}
                        >
                            <div
                                style={{
                                    backgroundColor: "#fff3cd",
                                    borderRadius: "8px",
                                    padding: "10px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    minWidth: "44px",
                                    minHeight: "44px",
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: "20px",
                                        color: "#856404",
                                    }}
                                >
                                    ⏰
                                </span>
                            </div>
                            <div style={{ flex: 1, minWidth: "120px" }}>
                                <div
                                    style={{
                                        fontSize: "13px",
                                        color: "#6c757d",
                                        marginBottom: "4px",
                                        fontWeight: "500",
                                    }}
                                >
                                    {t("pending") || "Pendientes"}
                                </div>
                                <div
                                    style={{
                                        fontSize: "24px",
                                        fontWeight: "bold",
                                        color: "#495057",
                                        lineHeight: "1.2",
                                    }}
                                >
                                    {
                                        filteredData.filter(
                                            (item) =>
                                                item.processed === "pending" ||
                                                item.processed === false ||
                                                item.processed === null
                                        ).length
                                    }
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Approved Card */}
                    <div
                        style={{
                            backgroundColor: "white",
                            borderRadius: "12px",
                            padding: "16px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                            border: "1px solid #e9ecef",
                            minHeight: "100px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                                flexWrap: "wrap",
                            }}
                        >
                            <div
                                style={{
                                    backgroundColor: "#d4edda",
                                    borderRadius: "8px",
                                    padding: "10px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    minWidth: "44px",
                                    minHeight: "44px",
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: "20px",
                                        color: "#155724",
                                    }}
                                >
                                    ✅
                                </span>
                            </div>
                            <div style={{ flex: 1, minWidth: "120px" }}>
                                <div
                                    style={{
                                        fontSize: "13px",
                                        color: "#6c757d",
                                        marginBottom: "4px",
                                        fontWeight: "500",
                                    }}
                                >
                                    {t("approved") || "Aprobadas"}
                                </div>
                                <div
                                    style={{
                                        fontSize: "24px",
                                        fontWeight: "bold",
                                        color: "#495057",
                                        lineHeight: "1.2",
                                    }}
                                >
                                    {
                                        filteredData.filter(
                                            (item) =>
                                                item.processed === "approved" ||
                                                item.processed === true ||
                                                item.processed === "completed"
                                        ).length
                                    }
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Total Approved Amount Card */}
                    <div
                        style={{
                            backgroundColor: "white",
                            borderRadius: "12px",
                            padding: "16px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                            border: "1px solid #e9ecef",
                            minHeight: "100px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                                flexWrap: "wrap",
                            }}
                        >
                            <div
                                style={{
                                    backgroundColor: "#cce7ff",
                                    borderRadius: "8px",
                                    padding: "10px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    minWidth: "44px",
                                    minHeight: "44px",
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: "20px",
                                        color: "#0056b3",
                                    }}
                                >
                                    💰
                                </span>
                            </div>
                            <div style={{ flex: 1, minWidth: "120px" }}>
                                <div
                                    style={{
                                        fontSize: "13px",
                                        color: "#6c757d",
                                        marginBottom: "4px",
                                        fontWeight: "500",
                                    }}
                                >
                                    {t("total_approved") || "Total Aprobado"}
                                </div>
                                <div
                                    style={{
                                        fontSize: "20px",
                                        fontWeight: "bold",
                                        color: "#495057",
                                        lineHeight: "1.2",
                                        wordBreak: "break-word",
                                    }}
                                >
                                    $
                                    {filteredData
                                        .filter(
                                            (item) =>
                                                item.processed === "approved" ||
                                                item.processed === true ||
                                                item.processed === "completed"
                                        )
                                        .reduce(
                                            (sum, item) =>
                                                sum +
                                                parseFloat(item.amount || 0),
                                            0
                                        )
                                        .toFixed(2)}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Total Requests Card */}
                    <div
                        style={{
                            backgroundColor: "white",
                            borderRadius: "12px",
                            padding: "16px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                            border: "1px solid #e9ecef",
                            minHeight: "100px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                                flexWrap: "wrap",
                            }}
                        >
                            <div
                                style={{
                                    backgroundColor: "#e6d7ff",
                                    borderRadius: "8px",
                                    padding: "10px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    minWidth: "44px",
                                    minHeight: "44px",
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: "20px",
                                        color: "#6f42c1",
                                    }}
                                >
                                    👥
                                </span>
                            </div>
                            <div style={{ flex: 1, minWidth: "120px" }}>
                                <div
                                    style={{
                                        fontSize: "13px",
                                        color: "#6c757d",
                                        marginBottom: "4px",
                                        fontWeight: "500",
                                    }}
                                >
                                    {t("total_requests") || "Total Solicitudes"}
                                </div>
                                <div
                                    style={{
                                        fontSize: "24px",
                                        fontWeight: "bold",
                                        color: "#495057",
                                        lineHeight: "1.2",
                                    }}
                                >
                                    {filteredData.length}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <MaterialTable
                title=""
                columns={columns}
                style={{
                    direction: isRTL === "rtl" ? "rtl" : "ltr",
                    borderRadius: "8px",
                    boxShadow: "4px 4px 6px #9E9E9E",
                }}
                data={filteredData}
                onRowClick={(evt, selectedRow) =>
                    setSelectedRow(selectedRow.tableData.id)
                }
                options={{
                    search: true,
                    searchFieldAlignment: "right",
                    searchAutoFocus: false,
                    filtering: false,
                    customSearch: (query, rowData) => {
                        console.log("query ke ", query);
                        if (!query || query.trim() === "") return true;

                        const searchLower = query.toLowerCase().trim();

                        // Search in all fields including raw status values
                        return (
                            // User info
                            (rowData.name &&
                                rowData.name
                                    .toLowerCase()
                                    .includes(searchLower)) ||
                            (rowData.driver_name &&
                                rowData.driver_name
                                    .toLowerCase()
                                    .includes(searchLower)) ||
                            (rowData.requestedByEmail &&
                                rowData.requestedByEmail
                                    .toLowerCase()
                                    .includes(searchLower)) ||
                            (rowData.email &&
                                rowData.email
                                    .toLowerCase()
                                    .includes(searchLower)) ||
                            (rowData.requestedByMobile &&
                                rowData.requestedByMobile
                                    .toLowerCase()
                                    .includes(searchLower)) ||
                            (rowData.userType &&
                                rowData.userType
                                    .toLowerCase()
                                    .includes(searchLower)) ||
                            // Transaction info
                            (rowData.transactionId &&
                                rowData.transactionId
                                    .toLowerCase()
                                    .includes(searchLower)) ||
                            (rowData.transaction_id &&
                                rowData.transaction_id
                                    .toLowerCase()
                                    .includes(searchLower)) ||
                            (rowData.id &&
                                rowData.id
                                    .toString()
                                    .toLowerCase()
                                    .includes(searchLower)) ||
                            // Amount
                            (rowData.amount &&
                                rowData.amount
                                    .toString()
                                    .includes(searchLower)) ||
                            // Status search - search in raw status values (server-side)
                            (t(rowData.processed) &&
                                t(rowData.processed)
                                    .toLowerCase()
                                    .includes(searchLower)) ||
                            // Bank info
                            (rowData.bankData &&
                                rowData.bankData.accountNumber &&
                                rowData.bankData.accountNumber
                                    .toLowerCase()
                                    .includes(searchLower)) ||
                            (rowData.bankData &&
                                rowData.bankData.bankName &&
                                rowData.bankData.bankName
                                    .toLowerCase()
                                    .includes(searchLower)) ||
                            // Date info
                            (rowData.requestDate &&
                                rowData.requestDate
                                    .toLowerCase()
                                    .includes(searchLower)) ||
                            (rowData.date &&
                                moment(rowData.date)
                                    .format("YYYY-MM-DD")
                                    .includes(searchLower)) ||
                            (rowData.approvedAt &&
                                moment(rowData.approvedAt)
                                    .format("YYYY-MM-DD")
                                    .includes(searchLower)) ||
                            (rowData.rejectedAt &&
                                moment(rowData.rejectedAt)
                                    .format("YYYY-MM-DD")
                                    .includes(searchLower))
                        );
                    },
                    exportCsv: (columns, data) => {
                        // Custom export untuk data withdraws dengan format yang lebih baik
                        const exportData = data.map((row) => {
                            return {
                                "Transaction ID":
                                    row.transactionId ||
                                    row.transaction_id ||
                                    row.id ||
                                    "N/A",
                                "Driver Name":
                                    row.name || row.driver_name || "N/A",
                                Email:
                                    row.requestedByEmail || row.email || "N/A",
                                Mobile: row.requestedByMobile || "N/A",
                                "User Type": row.userType || "N/A",
                                Amount: `$${parseFloat(row.amount || 0).toFixed(
                                    2
                                )}`,
                                "Request Date":
                                    row.requestDate ||
                                    (row.date
                                        ? moment(row.date).format("YYYY-MM-DD")
                                        : "N/A"),
                                Status: (() => {
                                    const status = row.processed;
                                    if (
                                        status === "pending" ||
                                        status === false ||
                                        status === null
                                    )
                                        return "Pending";
                                    if (
                                        status === "approved" ||
                                        status === true ||
                                        status === "completed"
                                    )
                                        return "Approved";
                                    if (status === "rejected")
                                        return "Rejected";
                                    return "Unknown";
                                })(),
                                "Approval Date": row.approvedAt
                                    ? moment(row.approvedAt).format(
                                          "YYYY-MM-DD HH:mm"
                                      )
                                    : "N/A",
                                "Approved By": row.approvedByName || "N/A",
                                "Rejection Date": row.rejectedAt
                                    ? moment(row.rejectedAt).format(
                                          "YYYY-MM-DD HH:mm"
                                      )
                                    : "N/A",
                                "Rejected By": row.rejectedByName || "N/A",
                                "Rejection Reason": row.rejectReason || "N/A",
                                "Bank Account":
                                    row.bankData?.accountNumber || "N/A",
                                "Bank Name": row.bankData?.bankName || "N/A",
                            };
                        });

                        // Convert to CSV
                        const headers = Object.keys(exportData[0] || {});
                        const csvContent = [
                            headers.join(","),
                            ...exportData.map((row) =>
                                headers
                                    .map((header) => {
                                        const value = row[header];
                                        // Escape commas and quotes in CSV
                                        if (
                                            typeof value === "string" &&
                                            (value.includes(",") ||
                                                value.includes('"'))
                                        ) {
                                            return `"${value.replace(
                                                /"/g,
                                                '""'
                                            )}"`;
                                        }
                                        return value;
                                    })
                                    .join(",")
                            ),
                        ].join("\n");

                        // Download CSV
                        downloadCsv(
                            csvContent,
                            `withdraws_${moment().format(
                                "YYYY-MM-DD_HH-mm"
                            )}.csv`
                        );
                    },
                    exportButton: {
                        csv: true, // Always enable CSV export
                        pdf: false,
                    },
                    rowStyle: (rowData) => ({
                        backgroundColor:
                            selectedRow === rowData.tableData.id
                                ? colors.THIRDCOLOR
                                : colors.WHITE,
                    }),
                    editable: {
                        backgroundColor: colors.WHITE,
                        fontSize: "0.8em",
                        fontWeight: "bold ",
                        fontFamily: 'Lucida Console", "Courier New", monospace',
                    },
                    headerStyle: {
                        position: "sticky",
                        top: "0px",
                        backgroundColor: colors.SECONDARY,
                        color: colors.WHITE,
                        fontSize: "0.8em",
                        fontWeight: "bold ",
                        fontFamily: 'Lucida Console", "Courier New", monospace',
                    },
                    // Custom toolbar dengan filter status
                    toolbar: true,
                    toolbarButtonAlignment: "left",
                }}
                components={{}}
                localization={{
                    toolbar: {
                        searchText: t("search") || "Search",
                        exportTitle: t("export"),
                    },
                    header: {
                        actions: t("actions"),
                    },
                    pagination: {
                        labelDisplayedRows:
                            "{from}-{to} " + t("of") + " {count}",
                        firstTooltip: t("first_page_tooltip"),
                        previousTooltip: t("previous_page_tooltip"),
                        nextTooltip: t("next_page_tooltip"),
                        lastTooltip: t("last_page_tooltip"),
                    },
                }}
            />
            {/* Wallet History Modal */}
            <WalletHistoryModal
                open={walletHistoryModalOpen}
                onClose={() => setWalletHistoryModalOpen(false)}
                userId={walletHistoryModalProps.userId}
                withdrawAmount={walletHistoryModalProps.withdrawAmount}
                userName={walletHistoryModalProps.userName}
                withdrawDate={walletHistoryModalProps.withdrawDate}
            />
            <AlertDialog open={commonAlert.open} onClose={cancelOperation}>
                {commonAlert.msg}
            </AlertDialog>

            {/* Confirmation Dialog */}
            <Dialog
                open={confirmDialog.open}
                onClose={handleConfirmClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {confirmDialog.action === "approve"
                        ? t("approve_withdrawal") || "Approve Withdrawal"
                        : t("reject_withdrawal") || "Reject Withdrawal"}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        {confirmDialog.withdraw && (
                            <>
                                <Typography variant="body1" sx={{ mb: 2 }}>
                                    <strong>{t("amount") || "Amount"}:</strong>{" "}
                                    ${confirmDialog.withdraw.amount}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ mb: 2, color: "text.secondary" }}
                                >
                                    <strong>{t("user") || "User"}:</strong>{" "}
                                    {confirmDialog.withdraw.name ||
                                        confirmDialog.withdraw.userId}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ mb: 2, color: "text.secondary" }}
                                >
                                    <strong>
                                        {t("transaction_id") ||
                                            "Transaction ID"}
                                        :
                                    </strong>{" "}
                                    {confirmDialog.withdraw.transactionId ||
                                        confirmDialog.withdraw.id}
                                </Typography>

                                {confirmDialog.action === "reject" && (
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        label={
                                            t("rejection_reason") ||
                                            "Rejection Reason"
                                        }
                                        value={confirmDialog.reason}
                                        onChange={(e) =>
                                            setConfirmDialog((prev) => ({
                                                ...prev,
                                                reason: e.target.value,
                                            }))
                                        }
                                        placeholder={
                                            t("rejection_reason_placeholder") ||
                                            "Please provide a clear reason for rejecting this withdrawal..."
                                        }
                                        required
                                        sx={{ mb: 2 }}
                                    />
                                )}
                            </>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleConfirmClose} color="inherit">
                        {t("cancel") || "Cancel"}
                    </Button>
                    <Button
                        onClick={handleConfirmAction}
                        color={
                            confirmDialog.action === "approve"
                                ? "primary"
                                : "error"
                        }
                        variant="contained"
                        disabled={
                            confirmDialog.action === "reject" &&
                            !confirmDialog.reason.trim()
                        }
                    >
                        {confirmDialog.action === "approve"
                            ? t("approve") || "Approve"
                            : t("reject") || "Reject"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Alert Dialog */}
            <AlertDialog
                open={alertDialog.open}
                onClose={closeAlert}
                title={alertDialog.title}
                message={alertDialog.message}
                type={alertDialog.type}
            />

            {/* User Details Modal */}
            {userDetailsModal.open && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 1000,
                    }}
                >
                    <div
                        style={{
                            backgroundColor: "white",
                            borderRadius: "12px",
                            padding: "24px",
                            maxWidth: "600px",
                            width: "90%",
                            maxHeight: "80vh",
                            overflow: "auto",
                            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.3)",
                        }}
                    >
                        {/* Header */}
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "20px",
                                borderBottom: "2px solid #f0f0f0",
                                paddingBottom: "15px",
                            }}
                        >
                            <h2
                                style={{
                                    margin: 0,
                                    color: "#333",
                                    fontSize: "24px",
                                }}
                            >
                                👤 {t("user_details") || "User Details"}
                            </h2>
                            <button
                                onClick={closeUserDetailsModal}
                                style={{
                                    background: "none",
                                    border: "none",
                                    fontSize: "24px",
                                    cursor: "pointer",
                                    color: "#666",
                                    padding: "5px",
                                }}
                            >
                                ✕
                            </button>
                        </div>

                        {/* User Information */}
                        <div style={{ marginBottom: "24px" }}>
                            <h3
                                style={{
                                    color: "#1976d2",
                                    marginBottom: "15px",
                                    fontSize: "18px",
                                }}
                            >
                                📋{" "}
                                {t("personal_information") ||
                                    "Personal Information"}
                            </h3>
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: "15px",
                                }}
                            >
                                <div>
                                    <label
                                        style={{
                                            fontWeight: "bold",
                                            color: "#666",
                                            fontSize: "12px",
                                        }}
                                    >
                                        {t("full_name") || "Full Name"}
                                    </label>
                                    <div
                                        style={{
                                            fontSize: "16px",
                                            color: "#333",
                                            marginTop: "5px",
                                        }}
                                    >
                                        {userDetailsModal.userData?.firstName}{" "}
                                        {userDetailsModal.userData?.lastName}
                                    </div>
                                </div>
                                <div>
                                    <label
                                        style={{
                                            fontWeight: "bold",
                                            color: "#666",
                                            fontSize: "12px",
                                        }}
                                    >
                                        {t("user_id") || "User ID"}
                                    </label>
                                    <div
                                        style={{
                                            fontFamily: "monospace",
                                            fontSize: "14px",
                                            color: "#1976d2",
                                            marginTop: "5px",
                                        }}
                                    >
                                        {userDetailsModal.userData?.uid}
                                    </div>
                                </div>
                                <div>
                                    <label
                                        style={{
                                            fontWeight: "bold",
                                            color: "#666",
                                            fontSize: "12px",
                                        }}
                                    >
                                        {t("email") || "Email"}
                                    </label>
                                    <div
                                        style={{
                                            fontSize: "16px",
                                            color: "#333",
                                            marginTop: "5px",
                                        }}
                                    >
                                        {userDetailsModal.userData?.email ||
                                            "N/A"}
                                    </div>
                                </div>
                                <div>
                                    <label
                                        style={{
                                            fontWeight: "bold",
                                            color: "#666",
                                            fontSize: "12px",
                                        }}
                                    >
                                        {t("mobile") || "Mobile"}
                                    </label>
                                    <div
                                        style={{
                                            fontSize: "16px",
                                            color: "#333",
                                            marginTop: "5px",
                                        }}
                                    >
                                        {userDetailsModal.userData?.mobile ||
                                            "N/A"}
                                    </div>
                                </div>
                                <div>
                                    <label
                                        style={{
                                            fontWeight: "bold",
                                            color: "#666",
                                            fontSize: "12px",
                                        }}
                                    >
                                        {t("user_type") || "User Type"}
                                    </label>
                                    <div
                                        style={{
                                            fontSize: "16px",
                                            color: "#333",
                                            marginTop: "5px",
                                        }}
                                    >
                                        {userDetailsModal.userData?.usertype ||
                                            "N/A"}
                                    </div>
                                </div>
                                <div>
                                    <label
                                        style={{
                                            fontWeight: "bold",
                                            color: "#666",
                                            fontSize: "12px",
                                        }}
                                    >
                                        {t("wallet_balance") ||
                                            "Wallet Balance"}
                                    </label>
                                    <div
                                        style={{
                                            fontSize: "16px",
                                            color: "#333",
                                            marginTop: "5px",
                                            fontWeight: "bold",
                                        }}
                                    >
                                        $
                                        {parseFloat(
                                            userDetailsModal.userData
                                                ?.walletBalance || 0
                                        ).toFixed(2)}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Bank Account Information - Using same mapping as BankAccount */}
                        {userDetailsModal.bankData && (
                            <div style={{ marginBottom: "24px" }}>
                                <h3
                                    style={{
                                        color: "#1976d2",
                                        marginBottom: "15px",
                                        fontSize: "18px",
                                    }}
                                >
                                    🏦{" "}
                                    {t("bank_account_details") ||
                                        "Bank Account Details"}
                                </h3>
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 1fr",
                                        gap: "15px",
                                    }}
                                >
                                    {/* Dynamic Bank Details - Show all filled fields like BankAccount */}
                                    {Object.entries(
                                        userDetailsModal.bankData
                                    ).map(([key, value]) => {
                                        // Skip system fields and empty values (same logic as BankAccount)
                                        if (
                                            [
                                                "id",
                                                "userId",
                                                "userType",
                                                "status",
                                                "createdAt",
                                                "updatedAt",
                                                "createdBy",
                                                "updatedBy",
                                            ].includes(key) ||
                                            !value ||
                                            value === ""
                                        ) {
                                            return null;
                                        }

                                        // Get field label (same mapping as BankAccount)
                                        let fieldLabel = key;
                                        const fieldLabels = {
                                            accountHolderName:
                                                t("account_holder_name") ||
                                                "Account Holder Name",
                                            accountNumber:
                                                t("account_numbers") ||
                                                "Account Numbers",
                                            iban: t("iban") || "IBAN",
                                            userName: t("name") || "Name",
                                            bankName:
                                                t("bank_name") || "Bank Name",
                                            bankCode:
                                                t("bank_code") || "Bank Code",
                                            accountType:
                                                t("account_type") ||
                                                "Account Type",
                                            routingNumber:
                                                t("routing_number") ||
                                                "Routing Number",
                                            swiftCode:
                                                t("swift_code") || "Swift Code",
                                            bankAddress:
                                                t("bank_address") ||
                                                "Bank Address",
                                            currency:
                                                t("currency") || "Currency",
                                            country: t("country") || "Country",
                                            recipientAddress:
                                                t("recipient_address") ||
                                                "Recipient Address",
                                            emailOrPhone:
                                                t("email_or_phone") ||
                                                "Email/Phone",
                                            // Custom fields mapping
                                            custom_1755508294856_5d10130oq:
                                                t("national_id") ||
                                                "National ID",
                                            custom_1755522725259_30v1o8y9c:
                                                t("cbu") || "CBU",
                                            custom_1755531620827_vl266hwra:
                                                t("passport_no") ||
                                                "Passport No.",
                                        };
                                        fieldLabel = fieldLabels[key] || key;

                                        // Display all data without masking (same as BankAccount)
                                        let displayValue = value;

                                        return (
                                            <div key={key}>
                                                <label
                                                    style={{
                                                        fontWeight: "bold",
                                                        color: "#666",
                                                        fontSize: "12px",
                                                    }}
                                                >
                                                    {fieldLabel}
                                                </label>
                                                <div
                                                    style={{
                                                        fontSize: "16px",
                                                        marginTop: "5px",
                                                        fontFamily:
                                                            key ===
                                                            "accountNumber"
                                                                ? "monospace"
                                                                : "inherit",
                                                        color:
                                                            key ===
                                                            "accountNumber"
                                                                ? "#1976d2"
                                                                : "#333",
                                                    }}
                                                >
                                                    {displayValue}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* No Bank Data Message */}
                        {!userDetailsModal.bankData && (
                            <div
                                style={{
                                    padding: "20px",
                                    backgroundColor: "#f8f9fa",
                                    borderRadius: "8px",
                                    textAlign: "center",
                                    color: "#666",
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: "18px",
                                        marginBottom: "10px",
                                    }}
                                >
                                    🏦
                                </div>
                                <div>
                                    {t("no_bank_data_found") ||
                                        "No bank account details found for this user."}
                                </div>
                                <div
                                    style={{
                                        fontSize: "14px",
                                        marginTop: "5px",
                                    }}
                                >
                                    {t("bank_data_setup_incomplete") ||
                                        "User may not have completed bank account setup."}
                                </div>
                            </div>
                        )}

                        {/* Footer Buttons */}
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: "12px",
                                marginTop: "24px",
                                borderTop: "2px solid #f0f0f0",
                                paddingTop: "20px",
                            }}
                        >
                            <button
                                onClick={closeUserDetailsModal}
                                style={{
                                    padding: "10px 20px",
                                    border: "2px solid #ddd",
                                    borderRadius: "6px",
                                    backgroundColor: "white",
                                    color: "#666",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                    fontWeight: "bold",
                                    transition: "all 0.2s",
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.borderColor = "#999";
                                    e.target.style.color = "#333";
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.borderColor = "#ddd";
                                    e.target.style.color = "#666";
                                }}
                            >
                                {t("close") || "Close"}
                            </button>
                            <button
                                onClick={() => {
                                    // Copy user details to clipboard - sama persis dengan yang ditampilkan
                                    const userInfo = `
                  ${t("user_details") || "User Details"}:
                  ${t("name") || "Name"}: ${
                                        userDetailsModal.userData?.firstName
                                    } ${userDetailsModal.userData?.lastName}
                  ${t("id") || "ID"}: ${userDetailsModal.userData?.uid}
                  ${t("email") || "Email"}: ${
                                        userDetailsModal.userData?.email ||
                                        "N/A"
                                    }
                  ${t("mobile") || "Mobile"}: ${
                                        userDetailsModal.userData?.mobile ||
                                        "N/A"
                                    }
                  ${t("type") || "Type"}: ${
                                        userDetailsModal.userData?.usertype ||
                                        "N/A"
                                    }
                  ${t("balance") || "Balance"}: $${parseFloat(
                                        userDetailsModal.userData
                                            ?.walletBalance || 0
                                    ).toFixed(2)}
                
                  ${t("bank_details") || "Bank Details"}:
${
    userDetailsModal.bankData
        ? (() => {
              // Gunakan field mapping yang sama dengan yang ditampilkan
              const fieldLabels = {
                  accountHolderName:
                      t("account_holder_name") || "Account Holder Name",
                  accountNumber: t("account_numbers") || "Account Numbers",
                  iban: t("iban") || "IBAN",
                  userName: t("name") || "Name",
                  bankName: t("bank_name") || "Bank Name",
                  bankCode: t("bank_code") || "Bank Code",
                  accountType: t("account_type") || "Account Type",
                  routingNumber: t("routing_number") || "Routing Number",
                  swiftCode: t("swift_code") || "Swift Code",
                  bankAddress: t("bank_address") || "Bank Address",
                  currency: t("currency") || "Currency",
                  country: t("country") || "Country",
                  recipientAddress:
                      t("recipient_address") || "Recipient Address",
                  emailOrPhone: t("email_or_phone") || "Email/Phone",
                  // Custom fields mapping
                  custom_1755508294856_5d10130oq:
                      t("national_id") || "National ID",
                  custom_1755522725259_30v1o8y9c: t("cbu") || "CBU",
                  custom_1755531620827_vl266hwra:
                      t("passport_no") || "Passport No.",
              };

              // Filter dan format bank data sama seperti yang ditampilkan
              const bankDetails = Object.entries(userDetailsModal.bankData)
                  .filter(([key, value]) => {
                      // Skip system fields and empty values (sama logic dengan display)
                      return (
                          ![
                              "id",
                              "userId",
                              "userType",
                              "status",
                              "createdAt",
                              "updatedAt",
                              "createdBy",
                              "updatedBy",
                          ].includes(key) &&
                          value &&
                          value !== ""
                      );
                  })
                  .map(([key, value]) => {
                      const fieldLabel = fieldLabels[key] || key;
                      return `${fieldLabel}: ${value}`;
                  })
                  .join("\n");

              return (
                  bankDetails ||
                  t("no_bank_details_available") ||
                  "No bank details available"
              );
          })()
        : t("no_bank_details_available") || "No bank details available"
}
                    `.trim();

                                    navigator.clipboard
                                        .writeText(userInfo)
                                        .then(() => {
                                            alert(
                                                t("user_details_copied") ||
                                                    "User details copied to clipboard!"
                                            );
                                        })
                                        .catch(() => {
                                            alert(
                                                t("copy_failed") ||
                                                    "Failed to copy to clipboard"
                                            );
                                        });
                                }}
                                style={{
                                    padding: "10px 20px",
                                    border: "none",
                                    borderRadius: "6px",
                                    backgroundColor: "#1976d2",
                                    color: "white",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                    fontWeight: "bold",
                                    transition: "background-color 0.2s",
                                }}
                                onMouseEnter={(e) =>
                                    (e.target.style.backgroundColor = "#1565c0")
                                }
                                onMouseLeave={(e) =>
                                    (e.target.style.backgroundColor = "#1976d2")
                                }
                            >
                                {t("copy_details") || "Copy Details"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add CSS animations */}
            <style jsx>{`
                @keyframes spin {
                    0% {
                        transform: rotate(0deg);
                    }
                    100% {
                        transform: rotate(360deg);
                    }
                }

                @keyframes pulse {
                    0%,
                    100% {
                        opacity: 1;
                    }
                    50% {
                        opacity: 0.5;
                    }
                }
            `}</style>
        </div>
    );
};

export default Withdraws;
